import cx from 'classnames';
import {useEffect, useRef, useState} from 'react';
import pluralize from 'pluralize';
import {createPropMap} from '@util/ArrayUtils';

const i18nText = upi.i18n.getTextFromKey;
const s = {
    border: 'border border-solid border-grey-lightest',
    borderOuter: 'border border-solid border-grey-lighter',
    tableScroll: 'relative w-full overflow-x-auto z-1 m-auto table-scrolling-horz',
    tableMain: 'w-full min-w-xl m-auto relative border-0 border-collapse text-sm',
    thSticky: 'sticky pin-t z-10 font-bold align-middle',
    cellSticky: 'sticky pin-l z-10 font-normal align-middle th-backdrop',
    th: 'text-left font-bold align-middle',
    td: 'relative align-middle',
    inner: 'flex flex-col p-2 w-32 md:w-48',
    center: 'flex justify-center'
}

const lblScrollable = i18nText( 'lbl-scrollable' );
const rowDisplayMap = {
    recyclability: i18nText( 'lbl-recyclability', 'Recyclability' ),
    componentType: i18nText( 'lbl-componentType', 'Component Type' ),
    componentMaterial: i18nText( 'lbl-componentMaterial', 'Component Material' ),
    componentWeight: i18nText( 'lbl-componentWeight', 'Full Weight of Source Material (grams)' ),
    amountOfSourceMaterial: i18nText( 'lbl-amountOfSourceMaterial', 'Amount of Source Material Used' ),
    componentColor: i18nText( 'lbl-componentColor', 'Plastic and Glass Component Color' ),
    compostableBiodegradablePlastic: i18nText( 'lbl-compostableBiodegradablePlastic',
        'Compostable/Biodegradable Plastic' ),
    recycledContent: i18nText( 'lbl-recycledContent', 'Recycled Content %' ),
    totalRecycledPlasticWeight: i18nText( 'lbl-totalRecycledPlasticWeight', 'Total Weight of Recycled Plastic' ),
    packagingType: i18nText( 'lbl-packagingType', 'Packaging Type' )
};

const TableRow = ( {rowId, rowData} ) => {
    const rowLabel = rowDisplayMap[rowId];
    if ( !rowLabel ) {
        return null;
    }

    return (
        <tr>
            <th className={cx( s.cellSticky, s.border, 'border-r-inset' )} role="heading">
                <div className={cx( s.inner, 'justify-end text-right' )}>
                    <span>{rowLabel}</span>
                </div>
            </th>
            {rowData.map( ( item, index ) =>
                <td className={cx( s.td, s.border )} key={`${rowId}-${index}`} role="cell">
                    <div className={cx( s.inner, 'items-center' )}>{formatTableCell( rowId, item )}</div>
                </td>
            )}
        </tr>
    );
}

const formatTableCell = ( id, value ) => {
    switch ( id ) {
        case 'recyclability':
            if ( !value ) {
                return <span className="icon-recycle-mobius-none text-xl mx-auto"></span>;
            }

            return value.map( ( symbol, index ) =>
                index % 2 === 0
                    ? <span data-mobius={symbol.toLowerCase()} className="icon-recycle-mobius text-xl m-1"></span>
                    : <span className={`icon-recycle-${symbol.toLowerCase()} text-xl m-1`}></span>
            );

        case 'onPackRecyclingLabel':
            return value ? 'Yes' : 'No';

        case 'componentWeight':
        case 'totalRecycledPlasticWeight':
            return isNaN( parseFloat( value ) ) ? 'N/A' : `${parseFloat( value )} ${pluralize( 'gram', value )}`;

        case 'recycledContent':
            return isNaN( parseInt( value ) ) ? 'N/A' : `${value}%`;

        default:
            return value || 'N/A';
    }
}

const transformPackagingData = ( packaging ) => {
    const data = {
        recyclability: packaging.recyclable ? packaging.recycleSymbols : null,
        ...packaging
    };

    delete data.recyclable;
    delete data.recycleSymbols;

    return data
}

const PackagingTable = ( {packaging} ) => {
    const [isScrollable, setIsScrollable] = useState( false );
    const tableRef = useRef();
    useEffect( () => {
        const scrollable = tableRef.current;
        const handleScroll = () => {
            scrollable.classList.add( 'has-scrolled' );
            offScroll();
        }
        const offScroll = () => {
            scrollable.removeEventListener( 'scroll', handleScroll );
        }

        function isScrollable ( element ) {
            return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
        }

        setIsScrollable( isScrollable( scrollable ) );
        scrollable.addEventListener( 'scroll', handleScroll );
        return offScroll;
    }, [packaging] );

    const propMap = createPropMap( packaging.map( transformPackagingData ) );

    return (
        <div data-testid="PackagingTable" ref={tableRef} className={cx( s.tableScroll, s.borderOuter )}>
            {isScrollable &&
                <div class="scroll-indicator" data-testid="PackagingTable-ScrollIndicator">
                    <span className="scroll-indicator-text">{lblScrollable}</span>
                </div>
            }
            <table className={s.tableMain}>
                <colgroup>
                    <col width={'96px'}/>
                </colgroup>
                <thead class="bg-white">
                    <tr>
                        <th className={cx( s.cellSticky, s.border, 'border-r-inset' )}>
                            <div className={cx( s.inner )}>&nbsp;</div>
                        </th>
                        {propMap( 'packDescription' ).map( ( label, index ) =>
                            <th scope="col" role="heading" className={cx( s.th, s.border )} key={index}>
                                <div className={s.inner}>{label}</div>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody class="bg-white">
                    {Object.keys( rowDisplayMap ).map( ( rowId, index ) =>
                        <TableRow rowId={rowId} rowData={propMap( rowId )} key={`${rowId}-${index}`}/>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default PackagingTable;
